import React, {useState, useEffect, useMemo} from 'react';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {
  useGetAllAgentScores,
  useGetCachedVideos,
  useGetAgentScoreExplanation,
} from '../../api/graphql_agent_scores';
import {
  AgentScore,
  AgentType,
  Video,
  VideoRating,
} from '../../types/generated/graphql';
import {usePageTitle} from '../../hooks/usePageTitle';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Collapse,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import _ from 'lodash';
import {
  pcolors,
  cardSx,
  pageSx,
  pageTitleSx,
  sectionLabelSx,
  scoreBadgeSx,
  tableHeadRowSx,
  tableHeadCellSx,
  tableBodyCellSx,
} from '../../theme/planapusTheme';
import {
  humanizeSpecialization,
  humanizeAgentType,
  humanizeVideoRating,
} from './agentLabels';

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
}

function RatingCell({rating}: {rating: VideoRating}) {
  const icon =
    rating === VideoRating.Beneficial ? (
      <CheckCircleIcon sx={{fontSize: 16, color: pcolors.green[600]}} />
    ) : rating === VideoRating.Harmful ? (
      <BlockIcon sx={{fontSize: 16, color: pcolors.red[600]}} />
    ) : (
      <RemoveCircleOutlineIcon sx={{fontSize: 16, color: pcolors.textMuted}} />
    );
  return (
    <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.75}}>
      {icon}
      <Typography sx={{fontSize: 12}}>{humanizeVideoRating(rating)}</Typography>
    </Box>
  );
}

function TypePill({type}: {type: AgentType}) {
  const isAnalysis = type === AgentType.Analysis;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        px: 1,
        py: 0.25,
        borderRadius: 1,
        fontSize: 11,
        fontWeight: 600,
        bgcolor: isAnalysis ? pcolors.blue[50] : '#F3E8FF',
        color: isAnalysis ? pcolors.blue[700] : pcolors.purple[500],
      }}
    >
      {humanizeAgentType(type)}
    </Box>
  );
}

function ScoreRow({
  score,
  video,
  client,
}: {
  score: AgentScore;
  video: Video | undefined;
  client: ApolloClient<NormalizedCacheObject>;
}) {
  const [open, setOpen] = useState(false);
  const {load, data: expData, loading: expLoading} = useGetAgentScoreExplanation(client);
  // Normalize to 0..1 for scoreBadgeSx — goodshowhq stores 0..10.
  const norm = Math.max(0, Math.min(1, score.score / 10));

  const handleToggle = () => {
    if (!open && !expData) {
      load({variables: {videoId: score.videoId}});
    }
    setOpen(!open);
  };

  const explanation =
    expData?.getScoresForVideo?.find((s: {id: string}) => s.id === score.id)?.explanation ?? null;

  return (
    <>
      <TableRow hover sx={{'& > *': {borderBottom: `1px solid ${pcolors.borderLight}`}}}>
        <TableCell sx={{...tableBodyCellSx, width: 36}}>
          <IconButton size="small" onClick={handleToggle}>
            {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={tableBodyCellSx}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25}}>
            {video?.thumbnails?.[0]?.url && (
              <Box
                component="img"
                src={video.thumbnails[0].url}
                alt=""
                sx={{width: 56, height: 32, objectFit: 'cover', borderRadius: 0.75, flex: 'none'}}
              />
            )}
            <Box sx={{minWidth: 0}}>
              <Typography sx={{fontSize: 13, fontWeight: 500, color: pcolors.textPrimary}} noWrap>
                {video?.title || 'Unknown video'}
              </Typography>
              <Typography sx={{fontSize: 11, color: pcolors.textSecondary}} noWrap>
                {video?.author?.name || '—'} · {score.videoId}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell sx={tableBodyCellSx}>{humanizeSpecialization(score.agent.specialization)}</TableCell>
        <TableCell sx={tableBodyCellSx}><TypePill type={score.type} /></TableCell>
        <TableCell sx={{...tableBodyCellSx, color: pcolors.textSecondary, fontFamily: 'monospace'}}>
          {score.agent.version}
        </TableCell>
        <TableCell sx={{...tableBodyCellSx, textAlign: 'center'}}>
          <Box sx={scoreBadgeSx(norm, score.scoreDirection)}>{score.score.toFixed(1)}</Box>
        </TableCell>
        <TableCell sx={tableBodyCellSx}><RatingCell rating={score.rating} /></TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{p: 0, borderBottom: 'none'}} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{p: 2, bgcolor: pcolors.bg}}>
              <Typography sx={sectionLabelSx}>Explanation</Typography>
              <Box sx={{mt: 1, ...cardSx, p: 1.5}}>
                {expLoading ? (
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <CircularProgress size={14} />
                    <Typography sx={{fontSize: 12, color: pcolors.textSecondary}}>
                      Loading explanation…
                    </Typography>
                  </Box>
                ) : (
                  <Typography sx={{fontSize: 13, color: pcolors.textPrimary, whiteSpace: 'pre-wrap'}}>
                    {explanation || 'No explanation provided.'}
                  </Typography>
                )}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function AdminAgentScores({client}: Props) {
  usePageTitle('Admin – Agent Scores');

  const [searchTerm, setSearchTerm] = useState('');
  const [filterSpec, setFilterSpec] = useState('');
  const [filterRating, setFilterRating] = useState('');
  const [filterType, setFilterType] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [limit, setLimit] = useState(500);

  const {data, loading, error} = useGetAllAgentScores(client, limit);

  const filteredScores = useMemo(() => {
    if (!data?.getAllAgentScores) return [];
    const q = searchTerm.toLowerCase();
    return data.getAllAgentScores.filter(score => {
      // Video-title/author search is deferred — metadata is only loaded for
      // the visible page, so searching on title would produce inconsistent
      // results. Video-ID search still works for all rows.
      const matchesSearch = !q || score.videoId.toLowerCase().includes(q);
      const matchesSpec = !filterSpec || score.agent.specialization === filterSpec;
      const matchesRating = !filterRating || score.rating === filterRating;
      const matchesType = !filterType || score.type === filterType;
      return matchesSearch && matchesSpec && matchesRating && matchesType;
    });
  }, [data, searchTerm, filterSpec, filterRating, filterType]);

  // Page-scoped: only fetch video metadata for the rows the user is
  // currently viewing. This turns "fetch 500 videos" into "fetch 25".
  const displayed = useMemo(
    () => filteredScores.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredScores, page, rowsPerPage],
  );
  const pageVideoIds = useMemo(
    () => _.uniq(displayed.map(s => s.videoId)),
    [displayed],
  );
  const {data: videoData} = useGetCachedVideos(client, pageVideoIds);
  const videoMap = useMemo(() => {
    const m: Record<string, Video> = {};
    videoData?.getCachedVideos?.forEach(v => (m[v.id] = v));
    return m;
  }, [videoData]);

  const specializations = useMemo(() => {
    if (!data?.getAllAgentScores) return [];
    return Array.from(new Set(data.getAllAgentScores.map(s => s.agent.specialization)));
  }, [data]);

  const stats = useMemo(() => {
    const avg =
      filteredScores.length > 0
        ? filteredScores.reduce((a, s) => a + s.score, 0) / filteredScores.length
        : 0;
    const harmful = filteredScores.filter(s => s.rating === VideoRating.Harmful).length;
    return {total: filteredScores.length, avg, harmful};
  }, [filteredScores]);

  if (loading && !data) {
    return (
      <Box sx={pageSx}>
        <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8}}>
          <CircularProgress size={24} />
          <Typography sx={{ml: 2, fontSize: 13, color: pcolors.textSecondary}}>
            Loading scores…
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={pageSx}>
        <Paper sx={{...cardSx, p: 2, borderLeft: `3px solid ${pcolors.red[500]}`}}>
          <Typography sx={{fontSize: 14, fontWeight: 600, color: pcolors.red[700]}}>
            Error loading agent scores
          </Typography>
          <Typography sx={{fontSize: 12, color: pcolors.textSecondary, mt: 0.5}}>{error.message}</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={pageSx}>
      <Box sx={{mb: 2}}>
        <Typography sx={pageTitleSx}>Agent scores</Typography>
        <Typography sx={{fontSize: 13, color: pcolors.textSecondary, mt: 0.5}}>
          Live agent output across every video, newest first. Expand a row for the model's explanation.
        </Typography>
      </Box>

      {/* Stats */}
      <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 2}}>
        <Paper sx={{...cardSx, p: 2}}>
          <Typography sx={sectionLabelSx}>Total scores</Typography>
          <Typography sx={{fontSize: 22, fontWeight: 700, mt: 0.5, fontVariantNumeric: 'tabular-nums'}}>
            {stats.total}
          </Typography>
        </Paper>
        <Paper sx={{...cardSx, p: 2}}>
          <Typography sx={sectionLabelSx}>Average score</Typography>
          <Typography sx={{fontSize: 22, fontWeight: 700, mt: 0.5, fontVariantNumeric: 'tabular-nums'}}>
            {stats.total > 0 ? stats.avg.toFixed(1) : '—'}
          </Typography>
        </Paper>
        <Paper sx={{...cardSx, p: 2}}>
          <Typography sx={sectionLabelSx}>Flagged harmful</Typography>
          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 700,
              mt: 0.5,
              fontVariantNumeric: 'tabular-nums',
              color: stats.harmful > 0 ? pcolors.red[700] : pcolors.textPrimary,
            }}
          >
            {stats.harmful}
          </Typography>
        </Paper>
      </Box>

      {/* Filters */}
      <Paper sx={{...cardSx, p: 2, mb: 2}}>
        <Box sx={{display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap'}}>
          <TextField
            size="small"
            placeholder="Search by video ID…"
            value={searchTerm}
            onChange={e => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            sx={{flex: 1, minWidth: 260}}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{fontSize: 18, color: pcolors.textMuted}} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{minWidth: 120}}>
            <InputLabel>Limit</InputLabel>
            <Select
              value={limit.toString()}
              label="Limit"
              onChange={e => setLimit(Number(e.target.value))}
            >
              {[100, 250, 500, 1000, 2000].map(n => (
                <MenuItem key={n} value={n} sx={{fontSize: 13}}>{n}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{minWidth: 200}}>
            <InputLabel>Specialization</InputLabel>
            <Select
              value={filterSpec}
              label="Specialization"
              onChange={(e: SelectChangeEvent) => {
                setFilterSpec(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="" sx={{fontSize: 13}}>All</MenuItem>
              {specializations.map(s => (
                <MenuItem key={s} value={s} sx={{fontSize: 13}}>{humanizeSpecialization(s)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{minWidth: 140}}>
            <InputLabel>Type</InputLabel>
            <Select
              value={filterType}
              label="Type"
              onChange={(e: SelectChangeEvent) => {
                setFilterType(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="" sx={{fontSize: 13}}>All</MenuItem>
              <MenuItem value={AgentType.Analysis} sx={{fontSize: 13}}>{humanizeAgentType(AgentType.Analysis)}</MenuItem>
              <MenuItem value={AgentType.Detection} sx={{fontSize: 13}}>{humanizeAgentType(AgentType.Detection)}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{minWidth: 140}}>
            <InputLabel>Rating</InputLabel>
            <Select
              value={filterRating}
              label="Rating"
              onChange={(e: SelectChangeEvent) => {
                setFilterRating(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="" sx={{fontSize: 13}}>All</MenuItem>
              <MenuItem value={VideoRating.Beneficial} sx={{fontSize: 13}}>{humanizeVideoRating(VideoRating.Beneficial)}</MenuItem>
              <MenuItem value={VideoRating.Neutral} sx={{fontSize: 13}}>{humanizeVideoRating(VideoRating.Neutral)}</MenuItem>
              <MenuItem value={VideoRating.Harmful} sx={{fontSize: 13}}>{humanizeVideoRating(VideoRating.Harmful)}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Table */}
      <Paper sx={{...cardSx, overflow: 'hidden'}}>
        <TableContainer sx={{maxHeight: 640}}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={tableHeadRowSx}>
                <TableCell sx={tableHeadCellSx} />
                <TableCell sx={tableHeadCellSx}>Video</TableCell>
                <TableCell sx={tableHeadCellSx}>Specialization</TableCell>
                <TableCell sx={tableHeadCellSx}>Type</TableCell>
                <TableCell sx={tableHeadCellSx}>Version</TableCell>
                <TableCell sx={{...tableHeadCellSx, textAlign: 'center'}}>Score</TableCell>
                <TableCell sx={tableHeadCellSx}>Rating</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayed.length > 0 ? (
                displayed.map(s => (
                  <ScoreRow key={s.id} score={s} video={videoMap[s.videoId]} client={client} />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} sx={{textAlign: 'center', py: 5, border: 'none'}}>
                    <Typography sx={{fontSize: 13, color: pcolors.textSecondary}}>
                      No scores match the selected filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredScores.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{borderTop: `1px solid ${pcolors.border}`, '& .MuiToolbar-root': {minHeight: 44}}}
        />
      </Paper>
    </Box>
  );
}
